<template>
   <b-container fluid="md" class="pt-1">
      <div class="standard-width mx-auto">
         <h1 class="page-title">{{ title }}</h1>
      </div>
      <div v-if="profile">
         <b-nav tabs class="card-tabs mx-auto standard-width">
            <b-nav-item
               v-for="section in sections"
               :key="section.key"
               :active="activeSection === section.key"
               @click="selectSection(section.key)"
               class="position-relative"
            >
               <b-icon-circle-fill
                  v-if="section.alert"
                  variant="danger"
                  font-scale="0.8"
                  class="tab-badge"
               />
               {{ section.label }}
            </b-nav-item>
         </b-nav>

         <b-card class="tabbed-card mx-auto standard-width mb-5">
            <!-- PROFILE -->
            <b-form @submit.prevent="onSubmitProfile" v-if="'profile' === activeSection">
               <!-- Name -->
               <b-form-row>
                  <b-col md="6">
                     <b-form-group
                        label="First Name"
                        label-for="input-first-name"
                        invalid-feedback="Required"
                     >
                        <b-form-input
                           id="input-first-name"
                           v-model="profile.firstName"
                           :state="profile.firstName ? !$v.profile.firstName.$invalid : null"
                           trim
                        ></b-form-input>
                     </b-form-group>
                  </b-col>
                  <b-col md="6">
                     <b-form-group
                        label="Last Name"
                        label-for="input-last-name"
                        invalid-feedback="Required"
                     >
                        <b-form-input
                           id="input-last-name"
                           v-model="profile.lastName"
                           :state="profile.lastName ? !$v.profile.lastName.$invalid : null"
                           trim
                        ></b-form-input>
                     </b-form-group>
                  </b-col>
               </b-form-row>
               <!-- Email -->
               <b-form-group
                  label="Email"
                  label-for="input-email"
                  invalid-feedback="Please enter a valid email"
                  description="This will be the user name for logging in."
               >
                  <b-form-input
                     id="input-email"
                     v-model="profile.email"
                     :state="profile.email ? !$v.profile.email.$invalid : null"
                     trim
                  ></b-form-input>
               </b-form-group>

               <!-- External Company -->
               <b-form-group
                  label="External Company"
                  label-for="select-external-company"
                  v-show="isExternalPartner"
               >
                  <b-form-input
                     id="select-external-company"
                     type="search"
                     v-model="profile.extPartnerCompany"
                     href="#"
                     tabindex="0"
                     ref="externalCompanyRef"
                     autocomplete="off"
                  ></b-form-input>
                  <b-popover
                     v-show="externalCompanies.length > 0"
                     target="select-external-company"
                     placement="bottomleft"
                     triggers="click blur"
                     :custom-class="`external-partner-popover ${
                        externalCompanies.length === 0 ? 'external-partner-popover--no-borders' : ''
                     }`"
                  >
                     <datalist class="d-flex flex-column external-partner-datalist">
                        <option
                           @click="onExternalCompanySelect(externalCompany)"
                           class="external-company-option text-truncate"
                           v-for="externalCompany in externalCompanies"
                           :key="externalCompany"
                        >
                           {{ externalCompany }}
                        </option>
                     </datalist>
                  </b-popover>
               </b-form-group>

               <!-- Job Title -->
               <b-form-group label="Job Title" label-for="input-job-title" v-if="isClient">
                  <b-form-input
                     id="input-job-title"
                     v-model="profile.title"
                     trim
                     list="datalist-titles"
                     autocomplete="off"
                  ></b-form-input>

                  <datalist id="datalist-titles" v-if="isClient">
                     <option v-for="title in companyTitles" :key="title">
                        {{ title }}
                     </option>
                  </datalist>
               </b-form-group>

               <!-- Department -->
               <b-form-group label="Department" label-for="input-department" v-if="isClient">
                  <b-form-input
                     id="input-department"
                     v-model="profile.department"
                     list="datalist-departments"
                     autocomplete="off"
                     trim
                  ></b-form-input>

                  <datalist id="datalist-departments" v-if="companyId">
                     <option v-for="department in companyDepartments" :key="department">
                        {{ department }}
                     </option>
                  </datalist>
               </b-form-group>

               <!-- Subsidiary -->
               <b-form-group
                  label="Subsidiary"
                  label-for="input-subsidiary"
                  v-if="isClient && !isExternalPartner"
               >
                  <b-form-input
                     id="input-subsidiary"
                     v-model="profile.subsidiary"
                     list="datalist-subsidiaries"
                     autocomplete="off"
                     trim
                  ></b-form-input>

                  <datalist id="datalist-subsidiaries" v-if="companyId">
                     <option v-for="subsidiary in companySubsidiaries" :key="subsidiary">
                        {{ subsidiary }}
                     </option>
                  </datalist>
               </b-form-group>

               <b-alert
                  variant="success"
                  dismissible
                  :show="profileAlertStatus === 'success'"
                  @dismissed="profileAlertStatus = null"
               >
                  Changes saved successfully.
               </b-alert>
               <b-alert
                  variant="danger"
                  dismissible
                  :show="profileAlertStatus === 'error'"
                  @dismissed="profileAlertStatus = null"
               >
                  Failed to save changes. Please try again.
               </b-alert>
               <div class="d-flex justify-content-end">
                  <b-button type="submit" variant="primary" :disabled="$v.profile.$invalid">
                     Save
                  </b-button>
               </div>
            </b-form>

            <!-- PROFILE QUESTIONNAIRE -->
            <div v-else-if="'about' === activeSection">
               <ProfileQuestionnaire
                  ref="profileQuestionnaire"
                  :user="profile"
                  @revert="reloadQuestionnaire"
                  :redirectOnSave="!!$route.query.first"
               />
            </div>

            <div v-else-if="'picture' === activeSection">
               <ProfilePictureUpload :userId="userId" />
            </div>

            <!-- CREDENCIALS -->
            <b-form @submit.prevent="onPasswordReset" v-else-if="'password' === activeSection">
               <div class="d-flex justify-content-end">
                  <b-button id="btn-request-password-reset" type="submit" variant="primary">
                     Request Password Reset
                  </b-button>
               </div>
            </b-form>

            <!-- COMPANY ROLES -->
            <b-form v-else-if="(isStaff || isExternalPartner) && 'roles' === activeSection">
               <b-table
                  borderless
                  thead-class="table-head"
                  :fields="roleFields"
                  :items="roles"
                  fixed
               >
                  <template v-if="!isExternalPartner" #head(actions)>
                     <div class="d-flex align-items-center justify-content-end">
                        <b-button
                           variant="primary"
                           size="sm"
                           class="action-btn"
                           v-b-modal.modal-add-assignment
                        >
                           Add Role
                        </b-button>
                     </div>
                  </template>
                  <template #head()="data">
                     <div class="d-flex align-items-center">
                        {{ data.label }}
                     </div>
                  </template>
                  <template #cell(companyId)="data">
                     {{ companyNameMap[data.value] }}
                  </template>
                  <template #cell(role)="data">
                     {{ roleMap[data.value] }}
                  </template>
                  <template v-if="!isExternalPartner" #cell(actions)="row">
                     <div class="d-flex align-items-center justify-content-end">
                        <div
                           :id="`btn-delete-role-${row.item.companyId}`"
                           class="delete-btn"
                           @click="removeAssignment(row.item.companyId)"
                        >
                           <b-icon
                              icon="x-square"
                              v-b-tooltip.left="'Remove this Assignment'"
                           ></b-icon>
                        </div>
                     </div>
                  </template>
               </b-table>

               <b-modal
                  id="modal-add-assignment"
                  title="Add Role"
                  hide-footer
                  centered
                  no-close-on-backdrop
                  no-close-on-esc
               >
                  <b-form @submit.prevent="onAssignmentSubmit">
                     <b-form-group label="Company" label-for="select-company">
                        <b-form-select
                           id="select-company"
                           :options="companySelectOptions"
                           v-model="newAssignment.companyId"
                        ></b-form-select>
                     </b-form-group>
                     <b-form-group label="Role" label-for="select-role">
                        <b-form-select
                           id="select-role"
                           :options="roleSelectOptions"
                           v-model="newAssignment.role"
                        ></b-form-select>
                     </b-form-group>
                     <b-row>
                        <b-col>
                           <b-button
                              type="submit"
                              variant="primary"
                              :disabled="$v.newAssignment.$invalid"
                           >
                              Submit
                           </b-button>
                        </b-col>
                     </b-row>
                  </b-form>
               </b-modal>
            </b-form>
            <!-- CREDENCIALS -->
            <b-form @submit.prevent="onSavePermissions" v-else-if="'permissions' === activeSection">
               <div>
                  <div class="permission-item">
                     <label>
                        Create employees & view/edit payroll values
                        <input type="checkbox" v-model="permissions.payroll" />
                     </label>
                  </div>
                  <div class="permission-item">
                     <label>
                        View and edit company revenue values
                        <input type="checkbox" v-model="permissions.revenue" />
                     </label>
                  </div>
                  <div class="permission-item">
                     <label>
                        Create contractors & view/edit payment values
                        <input type="checkbox" v-model="permissions.contractorPayments" />
                     </label>
                  </div>
               </div>
               <b-alert
                  variant="success"
                  dismissible
                  :show="permissionsAlertStatus === 'success'"
                  @dismissed="permissionsAlertStatus = null"
               >
                  Changes saved successfully.
               </b-alert>
               <b-alert
                  variant="danger"
                  dismissible
                  :show="permissionsAlertStatus === 'error'"
                  @dismissed="permissionsAlertStatus = null"
               >
                  Failed to save changes. Please try again.
               </b-alert>
               <div class="d-flex justify-content-end">
                  <b-button id="btn-request-password-reset" type="submit" variant="primary">
                     Save
                  </b-button>
               </div>
            </b-form>
         </b-card>
      </div>
   </b-container>
</template>

<script>
import {mapGetters} from 'vuex';
import {required, sameAs, email} from 'vuelidate/lib/validators';
import {passwordValidation} from '@/helpers/validation';
import {clientUserSuggestions} from '@/mixins';

import {UserGroups} from '@/store/modules/session';

import ProfilePictureUpload from './widgets/ProfilePictureUpload';
import ProfileQuestionnaire from '@/views/questionnaire/ProfileQuestionnaire';
export default {
   name: 'UserEdit',

   components: {
      ProfilePictureUpload,
      ProfileQuestionnaire,
   },
   mixins: [
      // Support for department and job title suggestions
      clientUserSuggestions,
   ],

   async created() {
      await this.fetchData();
      if (this.$route.query.section) {
         if (this.sections.some((section) => section.key === this.$route.query.section)) {
            this.activeSection = this.$route.query.section;
         }
      }
   },

   watch: {
      async $route() {
         await this.fetchData();
      },
   },

   data() {
      return {
         activeSection: 'profile',
         profile: null,
         profileAlertStatus: null,
         permissionsAlertStatus: null,
         credentials: {
            password: null,
            passwordConf: null,
         },
         permissionsExist: false,
         permissions: {
            payroll: false,
            payrollId: '',
            revenue: false,
            revenueId: '',
            contractorPayments: false,
            contractorPaymentsId: '',
         },
         companies: [],
         externalCompanies: [],
         roles: [],
         roleMap: {
            BM: 'Business Manager',
            PM: 'Project Manager',
            PA: 'Project Associate',
         },
         newAssignment: {
            companyId: null,
            role: null,
         },
         displayProfileQuestionnaire: false,
      };
   },

   computed: {
      ...mapGetters({
         userInfo: 'userInfo',
         isCustomer: 'isCustomer',
         isSME: 'isSME',
         isProfileQuestionnaireComplete: 'progress/isProfileQuestionnaireComplete',
      }),

      roleFields() {
         const fields = [
            {
               key: 'companyId',
               label: 'Company',
               sortable: true,
            },
            {key: 'role', sortable: true},
            'actions',
         ];

         if (this.isExternalPartner) {
            fields.pop();
         }
         return fields;
      },

      companyId() {
         return this.profile.companyId;
      },

      sections() {
         let sections = [
            {key: 'profile', label: 'Profile'},
            {key: 'picture', label: 'Picture'},
            {key: 'password', label: 'Password'},
         ];

         if (this.displayProfileQuestionnaire) {
            sections.splice(1, 0, {
               key: 'about',
               label: 'About You',
               alert: !this.isProfileQuestionnaireComplete,
            });
         }

         if (this.isStaff || this.isExternalPartner) {
            sections.push({key: 'roles', label: 'Roles'});
         }

         if (this.$route.name !== 'profile' && this.isClient && !this.isExternalPartner) {
            sections.push({key: 'permissions', label: 'Permissions'});
         }

         return sections;
      },

      title() {
         if (this.$route.name == 'profile') {
            return 'Update Your Profile';
         } else {
            return 'Update User Profile';
         }
      },

      isStaff() {
         return this.profile.group === UserGroups.STAFF;
      },

      isClient() {
         return (
            this.profile.group === UserGroups.CUSTOMER ||
            this.profile.group === UserGroups.SME ||
            this.profile.group === UserGroups.EXTERNAL_PARTNER
         );
      },

      isExternalPartner() {
         return this.profile.group === UserGroups.EXTERNAL_PARTNER;
      },

      userId() {
         if (this.$route.params.userId) {
            return this.$route.params.userId;
         } else {
            return this.$keycloak.subject;
         }
      },

      companyNameMap() {
         let map = {};
         this.companies.forEach((company) => {
            map[company.id] = company.companyName;
         });
         return map;
      },

      companySelectOptions() {
         return [
            {text: 'Please Select One', value: null},
            ...this.companies
               .map((company) => ({text: company.companyName, value: company.id}))
               .filter((option) => {
                  // filter out any companies where the user already has a role
                  return !this.roles.some((role) => role.companyId === option.value);
               }),
         ];
      },

      roleSelectOptions() {
         return [
            {text: 'Please Select One', value: null},
            ...Object.entries(this.roleMap).map((values) => {
               const [abbrev, name] = values;
               return {text: name, value: abbrev};
            }),
         ];
      },
   },

   methods: {
      /** Fetch profile data, and any other relevant data for this user */
      async fetchData() {
         const id = this.userId;
         this.permissionsExist = false;
         this.permissions = {
            payroll: false,
            payrollId: '',
            revenue: false,
            revenueId: '',
            contractorPayments: false,
            contractorPaymentsId: '',
         };

         let profile;
         try {
            profile = await this.blockingRequest('users/fetchProfile', {id});
            this.profile = profile;
         } catch (err) {
            this.$store.commit('showAlert', {
               response: err.response,
               fallbackMsg: 'Failed to fetch user profile',
               seconds: 5,
            });
            return;
         }

         if (profile.group === UserGroups.STAFF || profile.group === UserGroups.EXTERNAL_PARTNER) {
            this.$store.dispatch('users/fetchUserRoles', {id}).then((data) => {
               this.roles = data.results;
            });
            this.$store.dispatch('companies/fetchCompanies').then((companies) => {
               this.companies = companies;
            });
         }

         if (this.isExternalPartner) {
            this.$store.dispatch('companies/fetchExternalCompanies').then((externalCompanies) => {
               this.externalCompanies = externalCompanies;
            });
         }

         if (this.isClient) {
            this.loadSuggestions();

            try {
               await this.blockingRequest('questionnaire/loadProfileQuestionnaire', {
                  userId: this.profile.id,
               });
               this.displayProfileQuestionnaire = true;
            } catch (err) {
               console.log('Failed to fetch profile questionnaire');
            }
         }

         try {
            const permissionState = await this.$store.dispatch(
               'users/fetchUserPermissionEditState',
               {
                  userId: this.profile.id,
               }
            );
            this.permissionsExist = permissionState.permissionsExist;
            this.permissions = permissionState.permissions;
         } catch (e) {
            console.log(e);
         }
      },

      async onSavePermissions() {
         this.toggleDataLoading();
         try {
            if (this.permissionsExist) {
               await Promise.all([
                  this.$store.dispatch('users/updateUserPermission', {
                     permissionId: this.permissions.payrollId,
                     userId: this.profile.id,
                     resourceType: 'EMPLOYEE_PAYROLL',
                     hasAccess: this.permissions.payroll,
                     createdBy: this.profile.id,
                  }),
                  this.$store.dispatch('users/updateUserPermission', {
                     permissionId: this.permissions.revenueId,
                     userId: this.profile.id,
                     resourceType: 'GROSS_RECEIPTS',
                     hasAccess: this.permissions.revenue,
                     createdBy: this.profile.id,
                  }),
                  this.$store.dispatch('users/updateUserPermission', {
                     permissionId: this.permissions.contractorPaymentsId,
                     userId: this.profile.id,
                     resourceType: 'CONTRACTOR_PAYMENTS',
                     hasAccess: this.permissions.contractorPayments,
                     createdBy: this.profile.id,
                  }),
               ]);
            } else {
               await this.$store.dispatch('users/createUserPermissions', {
                  userId: this.profile.id,
                  payroll: this.permissions.payroll,
                  revenue: this.permissions.revenue,
                  contractorPayments: this.permissions.contractorPayments,
                  isInvitation: false,
               });
            }
            this.permissionsAlertStatus = 'success';
            setTimeout(() => {
               this.permissionsAlertStatus = null;
            }, 3000);
         } catch (e) {
            this.permissionsAlertStatus = 'error';
         } finally {
            this.toggleDataLoading();
         }
      },

      /** Handles external company select */
      onExternalCompanySelect(company) {
         this.profile.extPartnerCompany = company;
         this.$refs.externalCompanyRef.$el.click();
      },

      /** Change the active section */
      async selectSection(section) {
         if (this.activeSection === 'about') {
            const proceed = await this.$refs.profileQuestionnaire.checkSave(false);
            if (!proceed) {
               return;
            }
         }
         this.activeSection = section;
      },

      /** Submit an edit user request */
      async onSubmitProfile() {
         let id = this.$route.params.userId;
         if (!id) {
            id = this.$keycloak.subject;
         }

         const payload = {
            id,
            profile: this.profile,
         };

         try {
            await this.blockingRequest('users/updateProfile', payload);
            this.profileAlertStatus = 'success';
            setTimeout(() => {
               this.profileAlertStatus = null;
            }, 3000);
         } catch (err) {
            this.profileAlertStatus = 'error';
            this.$store.commit('showAlert', {
               response: err.response,
               fallbackMsg: 'Submission failed',
               seconds: 5,
            });
         }
      },

      /** Reload the profile questionnaire */
      async reloadQuestionnaire() {
         await this.$store.dispatch('questionnaire/loadProfileQuestionnaire', {
            userId: this.profile.id,
         });
      },

      /** Submit a password reset request */
      async onPasswordReset() {
         let id = null;
         if (this.$route.name == 'profile') {
            id = this.$keycloak.subject;
         } else if (this.$route.name == 'user-edit') {
            id = this.$route.params.userId;
         }

         try {
            await this.blockingRequest('users/resetPassword', {id});
            this.$router.push({name: 'Home'});
         } catch (err) {
            this.$store.commit('showAlert', {
               response: err.response,
               fallbackMsg: 'Submission failed',
               seconds: 5,
            });
         }
      },

      /** Remove a staff user's role with a company */
      async removeAssignment(companyId) {
         const payload = {
            companyId,
            userId: this.userId,
         };
         await this.$store.dispatch('companies/removeStaffAssignment', payload);

         const index = this.roles.map((role) => role.companyId).indexOf(companyId);
         if (index >= 0) {
            this.roles.splice(index, 1);
         }
      },

      /** Submit a create company role request */
      async onAssignmentSubmit() {
         const payload = {
            companyId: this.newAssignment.companyId,
            userId: this.userId,
            role: this.newAssignment.role,
         };
         await this.$store.dispatch('companies/addStaffAssignment', payload);

         this.roles.push(payload);
         this.$bvModal.hide('modal-add-assignment');
         this.newAssignment = {
            companyId: null,
            role: null,
         };
      },
   },

   async beforeRouteLeave(to, from, next) {
      if (this.activeSection === 'about') {
         const proceed = await this.$refs.profileQuestionnaire.checkSave(false);
         return next(proceed);
      }
      next();
   },

   validations: {
      profile: {
         firstName: {
            required,
         },
         lastName: {
            required,
         },
         email: {
            required,
            email,
         },
      },
      credentials: {
         password: passwordValidation,
         passwordConf: {
            required,
            sameAsPassword: sameAs('password'),
         },
      },
      newAssignment: {
         companyId: {
            required,
         },
         role: {
            required,
         },
      },
   },
};
</script>

<style lang="scss" scoped>
.tab-badge {
   position: absolute;
   top: -4px;
   right: -4px;
}
.external-partner-datalist {
   width: 274px;
}
.external-company-option {
   cursor: pointer;
   padding: 0.2rem 0.5rem;
   max-width: 274px;
   &:hover {
      background: $gray-400;
   }
}
.permission-item {
   margin-bottom: 15px;
}

input[type='checkbox'] {
   margin-left: 10px;
   transform: scale(1.2);
}
</style>
